import React from "react";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { PageHeader } from "@/components/Layout";
import { api } from "@/lib/api";
import { MapPin, Calendar, Users, ArrowUpRight } from "lucide-react";
import { Badge } from "@/components/ui/badge";

export default function Projects() {
    const q = useQuery({ queryKey: ["projects"], queryFn: () => api.get("/projects").then((r) => r.data) });
    const projects = q.data || [];

    return (
        <div data-testid="projects-page">
            <PageHeader
                eyebrow="Projects"
                title={<>On-ground, measurable, <span className="text-secondary italic font-light">documented.</span></>}
                sub="A live archive of our projects — past, ongoing and upcoming. Each one with locations, dates, beneficiaries and stories."
            />

            <section className="mx-auto max-w-7xl px-5 lg:px-10 py-16 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {projects.map((p) => (
                    <Link
                        key={p.id}
                        to={`/projects/${p.slug}`}
                        data-testid={`project-${p.slug}`}
                        className="group bg-card border border-border hover:border-primary/40 hover:-translate-y-1 transition-all"
                    >
                        <div className="aspect-[4/3] overflow-hidden bg-muted">
                            <img
                                src={p.cover_image}
                                alt={p.title}
                                loading="lazy"
                                className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                            />
                        </div>
                        <div className="p-6">
                            <div className="flex items-center justify-between mb-3">
                                <Badge
                                    variant="outline"
                                    className={`font-eyebrow text-[10px] rounded-full px-3 ${
                                        p.status === "ongoing"
                                            ? "bg-secondary/10 text-secondary border-secondary/30"
                                            : p.status === "completed"
                                            ? "bg-primary/10 text-primary border-primary/30"
                                            : "bg-muted text-muted-foreground"
                                    }`}
                                >
                                    {p.status}
                                </Badge>
                                <ArrowUpRight className="h-5 w-5 text-muted-foreground group-hover:text-primary group-hover:rotate-45 transition-all" />
                            </div>
                            <h3 className="font-display text-2xl leading-tight">{p.title}</h3>
                            <p className="mt-2 text-sm text-muted-foreground leading-relaxed line-clamp-2">{p.description}</p>
                            <div className="mt-5 grid grid-cols-2 gap-3 text-xs text-muted-foreground">
                                {p.location && (
                                    <div className="flex items-center gap-1.5">
                                        <MapPin className="h-3.5 w-3.5" />
                                        <span className="truncate">{p.location}</span>
                                    </div>
                                )}
                                {p.beneficiaries && (
                                    <div className="flex items-center gap-1.5">
                                        <Users className="h-3.5 w-3.5" />
                                        <span className="truncate">{p.beneficiaries}</span>
                                    </div>
                                )}
                            </div>
                        </div>
                    </Link>
                ))}
                {q.isLoading && <div>Loading…</div>}
                {!q.isLoading && projects.length === 0 && (
                    <div className="text-muted-foreground">No projects yet.</div>
                )}
            </section>
        </div>
    );
}
